import React, { Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useI18n } from 'hooks/useI18n';
import PageLayout from 'components/page-layout';
import NoAccess from 'components/no-access';
import ProtectedRoute from './protected-route';
import { ROUTES, SUPER_ADMIN_EMAILS, SUPER_ADMIN_ROUTES } from 'constants/app-constants';
import { canAccessAdminPath, getDeniedFeatureLabel } from 'helpers/menu-access';
import { Spin } from 'antd';
import PageLoader from 'components/page-loader';

// Lazy load pre-login pages
const Login = React.lazy(() => import('pages/pre_login_pages/login'));
const Register = React.lazy(() => import('pages/pre_login_pages/register'));
const OrganizationList = React.lazy(() => import('pages/organization'));

const AccessibleRoute = ({ Component, route }) => {
	const loginRedux = useSelector((state) => state?.loginRedux);
	const { t } = useI18n();

	if (!canAccessAdminPath(route, loginRedux)) {
		return <NoAccess featureName={getDeniedFeatureLabel(route, t)} />;
	}

	return (
		<Suspense fallback={<Spin />}>
			<Component />
		</Suspense>
	);
};

const AppRoutes = () => {
	const loginRedux = useSelector((state) => state?.loginRedux);
	const isSuperAdmin = SUPER_ADMIN_EMAILS.includes(loginRedux?.email);
	const dynamicRoutes = isSuperAdmin ? SUPER_ADMIN_ROUTES : ROUTES;

	return (
		<div className="app_container">
			<Suspense fallback={<PageLoader />}>
				<Routes>
					{/* Public Routes */}
					<Route path="/login" element={<Login />} />
					<Route path="/register" element={<Register />} />

					{/* Protected Standalone Route */}
					<Route
						path="/branches"
						element={
							<ProtectedRoute>
								<OrganizationList />
							</ProtectedRoute>
						}
					/>

					{/* Main Routes under PageLayout */}
					<Route path="/" element={<PageLayout />}>
						{dynamicRoutes.map(({ route, Component }, index) => (
							<Route
								key={index}
								path={route}
								element={
									<ProtectedRoute>
										<AccessibleRoute route={route} Component={Component} />
									</ProtectedRoute>
								}
							/>
						))}

						{/* Fallback */}
						<Route path="*" element={<NoAccess variant="missing" />} />
					</Route>
				</Routes>
			</Suspense>
		</div>
	);
};

export default AppRoutes;
